import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export const BRIEFS_PAGE_SIZE = 20;

const fetchBriefs = async (status, page, search, mentionsMe, limit = BRIEFS_PAGE_SIZE, personId) => {
  const { data } = await axios.get("/briefs", {
    params: { status, page, limit, search: search || undefined, mentionsMe: mentionsMe || undefined, personId: personId || undefined },
  });
  return data;
};

// Just the count, for the Mentions tab's badge — independent of whatever
// tab/page/search is currently active, and of the main list query's own
// mentionsMe fetch (which would only reflect the count correctly if the
// Mentions tab happened to be selected with no search/paging applied).
// limit=1 since only `total` is needed, not the briefs themselves.
const fetchMentionsCount = async () => {
  const data = await fetchBriefs(undefined, 1, "", true, 1);
  return data.total;
};

const fetchBrief = async (briefId) => {
  const { data } = await axios.get(`/briefs/${briefId}`);
  return data;
};

const fetchWorkTracker = async () => {
  const { data } = await axios.get("/briefs/work-tracker");
  return data;
};

const fetchBriefComments = async (briefId) => {
  const { data } = await axios.get(`/briefs/${briefId}/comments`);
  return data.comments;
};

const createBriefComment = async ({ briefId, body, isUpdateRequest, attachments }) => {
  const { data } = await axios.post(`/briefs/${briefId}/comments`, { body, isUpdateRequest, attachments });
  return data;
};

const createBrief = async (payload) => {
  const { data } = await axios.post("/briefs", payload);
  return data;
};

const updateBrief = async ({ briefId, ...payload }) => {
  const { data } = await axios.patch(`/briefs/${briefId}`, payload);
  return data;
};

const deleteBrief = async ({ briefId }) => {
  const { data } = await axios.delete(`/briefs/${briefId}`);
  return data;
};

// Direct-to-S3 upload, shared by both the "editing an existing brief" and
// "drafting a new one" flows below. Two steps here —
//   1. Ask the backend for a presigned PUT URL per file (metadata only).
//   2. PUT each file straight to S3 with that URL, via plain fetch (not our
//      shared `axios` instance) — axios.defaults.headers.common carries
//      x-auth-token and other app headers that have no business going to S3,
//      and a presigned URL's signature can reject unexpected ones.
// Returns the resulting public URLs; persisting them is the caller's job,
// since that differs between the two flows (see below).
const putFilesToS3 = async (presignEndpoint, files) => {
  const { data: presignData } = await axios.post(presignEndpoint, {
    files: files.map((file) => ({ name: file.name, type: file.type, size: file.size })),
  });

  await Promise.all(
    presignData.files.map(({ uploadUrl }, i) => {
      const file = files[i];
      return fetch(uploadUrl, {
        method: "PUT",
        // Must exactly match the Content-Type the backend signed the URL
        // with (also file.type) — S3's signature covers it, so a mismatch
        // here fails as SignatureDoesNotMatch.
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      }).then((res) => {
        if (!res.ok) throw new Error(`Upload failed for "${file.name}" (${res.status})`);
      });
    })
  );

  return presignData.files.map((f) => f.publicUrl);
};

// Editing an already-saved brief: uploads persist immediately, since there's
// a real briefId to key the S3 objects and the attachments array under.
const uploadBriefAttachments = async ({ briefId, files }) => {
  const urls = await putFilesToS3(`/briefs/${briefId}/attachments/presign`, files);
  const { data } = await axios.post(`/briefs/${briefId}/attachments`, { urls });
  return data;
};

// Drafting a brand-new brief, before it has an id yet: uploads still go
// straight to S3 (scoped to this user's own "drafts" folder server-side —
// see draftScope in helpers/s3BriefAttachments.js), but there's no brief to
// save the URLs onto yet. Just returns them — the caller holds them in local
// state and sends them as part of the eventual POST /briefs payload, where
// the backend re-validates they're really this user's own draft uploads.
const uploadDraftAttachments = async (files) => {
  return putFilesToS3("/briefs/attachments/presign", files);
};

// Cleanup for a cancelled Create Brief session — deletes draft uploads from S3
// that never made it into a saved brief's attachments array.
const deleteDraftAttachments = async (urls) => {
  const { data } = await axios.delete("/briefs/attachments/draft", { data: { urls } });
  return data;
};

// A comment's own attachments — unlike the brief's, no separate persist
// step: the comment doesn't exist yet either way, so the resulting URLs
// just go straight into the eventual POST /:briefId/comments body alongside
// the comment's text (see createBriefComment above), the same way a
// brand-new brief's draft attachments get folded into its own create call.
const uploadCommentAttachments = async ({ briefId, files }) => {
  return putFilesToS3(`/briefs/${briefId}/comments/attachments/presign`, files);
};

const fetchUserLookup = async () => {
  const { data } = await axios.get("/users/lookup");
  return data;
};

const fetchCategoryLookup = async () => {
  const { data } = await axios.get("/category/lookup");
  return data;
};

// Same brand list /filter/influencers' own Brands filter is backed by — raw
// {_id, name} from that endpoint, normalized to {id, name} to match every
// other lookup here.
const fetchBrandLookup = async () => {
  const { data } = await axios.get("/filter/influencers/brands");
  return (data || []).map((b) => ({ id: String(b._id), name: b.name }));
};

// Custom hooks
export const useBriefs = (status = "all", page = 1, search = "", mentionsMe = false, enabled = true, personId = null) => {
  return useQuery({
    queryKey: ["briefs", status, page, search, mentionsMe, personId],
    queryFn: () => fetchBriefs(status === "all" ? undefined : status, page, search, mentionsMe, undefined, personId),
    placeholderData: { briefs: [], page, pages: 1, total: 0, isSuperUser: false },
    // Keeps the current page's cards on screen (instead of a loading flash)
    // while the next page fetches — v4's API for this, not a v5 fn-form placeholderData.
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
  });
};

// Badge count for the Mentions tab — a first, minimal step toward real
// notifications. Query key starts with "briefs" so it's covered by the same
// invalidation useCreateBriefComment already does on a successful post.
export const useMentionsCount = () => {
  return useQuery({
    queryKey: ["briefs", "mentions-count"],
    queryFn: fetchMentionsCount,
    placeholderData: 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Work Tracker tab — per-assignee workload breakdown, independent of
// whatever status/page/search the main list has active (it's a separate
// screen, not a filtered view of the card grid).
export const useWorkTracker = (enabled = true) => {
  return useQuery({
    queryKey: ["briefs", "work-tracker"],
    queryFn: fetchWorkTracker,
    enabled,
    staleTime: 60 * 1000, // shorter than the list's 5 min — a workload view is meant to be current
  });
};

// Single brief — the detail dialog's own fetch, independent of whatever
// page/filter/search the list happens to have loaded (needed for a fresh
// deep link via /brief/:briefId, which may not be on the list at all).
export const useBrief = (briefId) => {
  return useQuery({
    queryKey: ["brief", briefId],
    queryFn: () => fetchBrief(briefId),
    enabled: Boolean(briefId),
  });
};

export const useBriefComments = (briefId) => {
  return useQuery({
    queryKey: ["brief-comments", briefId],
    queryFn: () => fetchBriefComments(briefId),
    enabled: Boolean(briefId),
    placeholderData: [],
  });
};

export const useCreateBriefComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createBriefComment,
    onSuccess: (_data, { briefId }) => {
      queryClient.invalidateQueries({ queryKey: ["brief-comments", briefId] });
      // A new mention can change who sees this brief under the Mentions tab.
      queryClient.invalidateQueries({ queryKey: ["briefs"] });
    },
    onError: (error) => {
      console.error("Error posting brief comment:", error);
      throw error;
    },
  });
};

export const useCreateBrief = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createBrief,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["briefs"] });
    },
    onError: (error) => {
      console.error("Error creating brief:", error);
      throw error;
    },
  });
};

export const useUpdateBrief = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateBrief,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["briefs"] });
    },
    onError: (error) => {
      console.error("Error updating brief:", error);
      throw error;
    },
  });
};

export const useDeleteBrief = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteBrief,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["briefs"] });
    },
    onError: (error) => {
      console.error("Error deleting brief:", error);
      throw error;
    },
  });
};

export const useUploadBriefAttachments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadBriefAttachments,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["briefs"] });
    },
    onError: (error) => {
      console.error("Error uploading brief attachments:", error);
      throw error;
    },
  });
};

// For the Create Brief modal, before a brief id exists — no query to
// invalidate here, since nothing's persisted yet (see uploadDraftAttachments).
export const useUploadDraftAttachments = () => {
  return useMutation({
    mutationFn: uploadDraftAttachments,
    onError: (error) => {
      console.error("Error uploading draft attachments:", error);
      throw error;
    },
  });
};

// For the comment composer — no query to invalidate here either, for the
// same reason as useUploadDraftAttachments (nothing's persisted until the
// comment itself is posted, see useCreateBriefComment).
export const useUploadCommentAttachments = () => {
  return useMutation({
    mutationFn: uploadCommentAttachments,
    onError: (error) => {
      console.error("Error uploading comment attachments:", error);
      throw error;
    },
  });
};

export const useDeleteDraftAttachments = () => {
  return useMutation({
    mutationFn: deleteDraftAttachments,
    onError: (error) => {
      // Best-effort — the modal is already closing regardless of whether this
      // succeeds, so this just logs rather than surfacing an error to the user.
      console.error("Error deleting draft attachments:", error);
    },
  });
};

// Org member list for the Assignee/Reviewer pickers — rarely changes, so a long staleTime.
export const useUserLookup = () => {
  return useQuery({
    queryKey: ["users", "lookup"],
    queryFn: fetchUserLookup,
    placeholderData: [],
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Content-category taxonomy for the Category picker — static reference data, long staleTime.
export const useCategoryLookup = () => {
  return useQuery({
    queryKey: ["categories", "lookup"],
    queryFn: fetchCategoryLookup,
    placeholderData: [],
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Brand list for the Brand picker — same source as /filter/influencers' Brands
// filter, static reference data, long staleTime.
export const useBrandLookup = () => {
  return useQuery({
    queryKey: ["brands", "lookup"],
    queryFn: fetchBrandLookup,
    placeholderData: [],
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

export default {
  useBriefs,
  useMentionsCount,
  useBrief,
  useCreateBrief,
  useUpdateBrief,
  useDeleteBrief,
  useUploadBriefAttachments,
  useUploadDraftAttachments,
  useUploadCommentAttachments,
  useDeleteDraftAttachments,
  useBriefComments,
  useCreateBriefComment,
  useUserLookup,
  useCategoryLookup,
  useBrandLookup,
};
